import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { FaChartLine, FaBell, FaUsers, FaFileAlt, FaCertificate, FaCreditCard, FaDatabase, FaMemory, FaServer, FaHdd, FaClock, FaExclamationTriangle, FaCheckCircle, FaCog, FaShieldAlt, FaCalendarAlt } from 'react-icons/fa';
import { adminAPI } from '../../services/api';

const SuperAdminDashboard = () => {
  const { user } = useSelector((state) => state.auth);

  // Mock system metrics (could be replaced with real system monitoring later)
  const [systemMetrics, setSystemMetrics] = useState({
    cpuUsage: 45,
    memoryUsage: 67,
    diskUsage: 78,
    uptime: '7j 14h 32m',
    activeConnections: 234,
    responseTime: '145ms'
  });

  const [recentLogs, setRecentLogs] = useState([]);
  const [dbStats, setDbStats] = useState({
    users: 0,
    loveshots: 0,
    size: '0GB'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [logsResponse, statsResponse] = await Promise.all([
          adminAPI.getLogs({ limit: 10 }),
          adminAPI.getStats()
        ]);

        setRecentLogs(logsResponse.data);
        setDbStats({
          users: statsResponse.data.totalUsers,
          loveshots: statsResponse.data.totalLoveshots,
          size: '2.3GB' // Mock for now, could calculate real DB size
        });
      } catch (error) {
        console.error('Error fetching superadmin data:', error);
        // Fallback to mock data
        setRecentLogs([
          { id: 1, level: 'error', message: 'Failed login attempt from IP 192.168.1.100', time: 'il y a 2 min' },
          { id: 2, level: 'warning', message: 'High memory usage detected', time: 'il y a 5 min' },
          { id: 3, level: 'info', message: 'Database backup completed successfully', time: 'il y a 12 min' },
          { id: 4, level: 'info', message: 'New user registration: admin@chatoo.com', time: 'il y a 18 min' },
          { id: 5, level: 'error', message: 'Payment gateway timeout', time: 'il y a 25 min' }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Check if user is superadmin
  if (!user || user.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  const dashboardItems = [
    // Admin items
    {
      icon: FaChartLine,
      title: 'Analytics',
      description: 'Aperçu global et statistiques détaillées.',
      path: '/admin/dashboard',
      color: 'primary'
    },
    {
      icon: FaBell,
      title: 'Notifications',
      description: 'Gérer les notifications système.',
      path: '/admin/notifications',
      color: 'info'
    },
    {
      icon: FaUsers,
      title: 'Utilisateurs',
      description: 'Gérer et modérer les comptes utilisateurs.',
      path: '/admin/users',
      color: 'success'
    },
    {
      icon: FaFileAlt,
      title: 'Contenus',
      description: 'Modérer les loveshots et stories.',
      path: '/admin/content',
      color: 'warning'
    },
    {
      icon: FaCertificate,
      title: 'Certifications',
      description: 'Traiter les demandes de certification.',
      path: '/admin/certifications',
      color: 'secondary'
    },
    {
      icon: FaCreditCard,
      title: 'Paiements',
      description: 'Gérer les transactions et abonnements.',
      path: '/admin/payments',
      color: 'danger'
    },
    // Superadmin items
    {
      icon: FaFileAlt,
      title: 'Logs Système',
      description: 'Consulter et analyser les logs système.',
      path: '/superadmin/logs',
      color: 'dark'
    },
    {
      icon: FaMemory,
      title: 'Cache & Mémoire',
      description: 'Gérer les caches et la mémoire système.',
      path: '/superadmin/cache',
      color: 'info'
    },
    {
      icon: FaDatabase,
      title: 'Base de Données',
      description: 'Gérer les bases de données et migrations.',
      path: '/superadmin/database',
      color: 'primary'
    },
    {
      icon: FaShieldAlt,
      title: 'Sécurité',
      description: 'Paramètres de sécurité et monitoring.',
      path: '/superadmin/security',
      color: 'danger'
    },
    {
      icon: FaCog,
      title: 'Configuration',
      description: 'Paramètres système avancés.',
      path: '/superadmin/config',
      color: 'secondary'
    },
    {
      icon: FaServer,
      title: 'Serveurs',
      description: 'Gestion des serveurs et infrastructure.',
      path: '/superadmin/servers',
      color: 'success'
    }
  ];

  const getLogIcon = (level) => {
    switch (level) {
      case 'error': return <FaExclamationTriangle className="text-danger" />;
      case 'warning': return <FaExclamationTriangle className="text-warning" />;
      case 'info': return <FaCheckCircle className="text-info" />;
      default: return <FaCheckCircle className="text-secondary" />;
    }
  };

  return (
    <div className="superadmin-dashboard min-vh-100" style={{ backgroundColor: 'var(--chatoo-bg-secondary)' }}>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="h2 mb-1">Tableau de Bord Super Administrateur</h1>
                <p className="text-muted mb-0">Bienvenue, {user.name || 'Super Admin'} • {new Date().toLocaleDateString('fr-FR')}</p>
              </div>
              <div className="text-end">
                <FaCalendarAlt className="text-muted me-2" />
                <small className="text-muted">Dernière mise à jour: {new Date().toLocaleTimeString('fr-FR')}</small>
              </div>
            </div>
          </div>
        </div>

        {/* System Metrics */}
        <div className="row mb-4">
          <div className="col-12">
            <h5 className="mb-3">Métriques Système</h5>
          </div>
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaCog className="text-primary mb-2" size={24} />
                <h4 className="h5 mb-1">{systemMetrics.cpuUsage}%</h4>
                <small className="text-muted">CPU</small>
                <div className="progress mt-2" style={{height: '4px'}}>
                  <div className="progress-bar bg-primary" style={{width: `${systemMetrics.cpuUsage}%`}}></div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaMemory className="text-info mb-2" size={24} />
                <h4 className="h5 mb-1">{systemMetrics.memoryUsage}%</h4>
                <small className="text-muted">Mémoire</small>
                <div className="progress mt-2" style={{height: '4px'}}>
                  <div className="progress-bar bg-info" style={{width: `${systemMetrics.memoryUsage}%`}}></div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaHdd className="text-warning mb-2" size={24} />
                <h4 className="h5 mb-1">{systemMetrics.diskUsage}%</h4>
                <small className="text-muted">Disque</small>
                <div className="progress mt-2" style={{height: '4px'}}>
                  <div className="progress-bar bg-warning" style={{width: `${systemMetrics.diskUsage}%`}}></div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaClock className="text-success mb-2" size={24} />
                <h4 className="h5 mb-1">{systemMetrics.uptime}</h4>
                <small className="text-muted">Uptime</small>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaUsers className="text-secondary mb-2" size={24} />
                <h4 className="h5 mb-1">{systemMetrics.activeConnections}</h4>
                <small className="text-muted">Connexions</small>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaServer className="text-danger mb-2" size={24} />
                <h4 className="h5 mb-1">{systemMetrics.responseTime}</h4>
                <small className="text-muted">Temps de réponse</small>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="row mb-4">
          <div className="col-12">
            <h5 className="mb-3">Actions Rapides</h5>
          </div>
          {dashboardItems.map((item, index) => (
            <div key={index} className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
              <div className="card h-100 shadow-sm border-0 hover-card">
                <div className="card-body text-center">
                  <item.icon size={32} className={`text-${item.color} mb-3`} />
                  <h6 className="card-title mb-2">{item.title}</h6>
                  <p className="card-text text-muted small mb-3">{item.description}</p>
                  <button
                    className={`btn btn-${item.color} btn-sm w-100`}
                    onClick={() => window.location.href = item.path}
                  >
                    Accéder
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* System Status and Logs */}
        <div className="row">
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0">
                <h5 className="mb-0">Statut des Services</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-6 mb-3">
                    <div className="d-flex align-items-center">
                      <FaServer className="text-success me-3" size={20} />
                      <div>
                        <small className="text-muted d-block">Serveur Web</small>
                        <span className="badge bg-success">En ligne</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <div className="d-flex align-items-center">
                      <FaDatabase className="text-success me-3" size={20} />
                      <div>
                        <small className="text-muted d-block">Base de Données</small>
                        <span className="badge bg-success">OK</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <div className="d-flex align-items-center">
                      <FaMemory className="text-warning me-3" size={20} />
                      <div>
                        <small className="text-muted d-block">Cache Redis</small>
                        <span className="badge bg-warning">Avertissement</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <div className="d-flex align-items-center">
                      <FaShieldAlt className="text-success me-3" size={20} />
                      <div>
                        <small className="text-muted d-block">Sécurité</small>
                        <span className="badge bg-success">Active</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0">
                <h5 className="mb-0">Logs Récentes</h5>
              </div>
              <div className="card-body p-0">
                <div className="list-group list-group-flush" style={{maxHeight: '300px', overflowY: 'auto'}}>
                  {recentLogs.map((log) => (
                    <div key={log.id} className="list-group-item border-0 px-4 py-3">
                      <div className="d-flex align-items-start">
                        <div className="flex-shrink-0 me-3 mt-1">
                          {getLogIcon(log.level)}
                        </div>
                        <div className="flex-grow-1">
                          <p className="mb-1 small">{log.message}</p>
                          <small className="text-muted">{log.time}</small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Database and Cache Status */}
        <div className="row">
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0">
                <h6 className="mb-0">Statut Base de Données</h6>
              </div>
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-4">
                    <h4 className="h6 mb-1">15,420</h4>
                    <small className="text-muted">Utilisateurs</small>
                  </div>
                  <div className="col-4">
                    <h4 className="h6 mb-1">45,230</h4>
                    <small className="text-muted">Loveshots</small>
                  </div>
                  <div className="col-4">
                    <h4 className="h6 mb-1">2.3GB</h4>
                    <small className="text-muted">Taille DB</small>
                  </div>
                </div>
                <hr />
                <div className="mb-2">
                  <div className="d-flex justify-content-between mb-1">
                    <small className="text-muted">Connexions actives</small>
                    <small className="fw-bold">23/100</small>
                  </div>
                  <div className="progress" style={{height: '6px'}}>
                    <div className="progress-bar bg-success" style={{width: '23%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0">
                <h6 className="mb-0">Cache & Performance</h6>
              </div>
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-4">
                    <h4 className="h6 mb-1">94.2%</h4>
                    <small className="text-muted">Hit Rate</small>
                  </div>
                  <div className="col-4">
                    <h4 className="h6 mb-1">1.2GB</h4>
                    <small className="text-muted">Utilisé</small>
                  </div>
                  <div className="col-4">
                    <h4 className="h6 mb-1">145ms</h4>
                    <small className="text-muted">Avg Response</small>
                  </div>
                </div>
                <hr />
                <div className="mb-2">
                  <div className="d-flex justify-content-between mb-1">
                    <small className="text-muted">Utilisation cache</small>
                    <small className="fw-bold">67%</small>
                  </div>
                  <div className="progress" style={{height: '6px'}}>
                    <div className="progress-bar bg-info" style={{width: '67%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .hover-card:hover {
          transform: translateY(-2px);
          transition: transform 0.2s ease;
        }
        .min-vh-100 {
          min-height: 100vh;
        }
      `}</style>
    </div>
  );
};

export default SuperAdminDashboard;
